<script lang="ts" setup>
import { computed, onBeforeMount, ref } from 'vue';
import { ModalDialog, SecondaryButton } from '@hfc/ui-core';
import ServicesSection from '@/page/infrastructure/components/ServicesSection.ce.vue';
import GoogleReviews from '@/page/infrastructure/components/metalPlans/GoogleReviews.vue';
import ContactCard from '@/page/infrastructure/components/contactUs/ContactCard.vue';
import AccordionContent from '@/page/infrastructure/components/AccordionContent.vue';
import { useUrlHelper } from '@/page/infrastructure/composition/useUrlHelper';
import LicenseContent from '@/page/infrastructure/components/LicenseContent.vue';
import { usePageStore } from '@/page/infrastructure/composition/usePageStore';

const { pageState } = usePageStore();
const { getCdnUrl, getBaseUrl, getMediCalQuoteUrl, getIaFQuoteUrl, getEnrollmentAppUri, getMedicareAppointmentUrl, getSmallBusinessConsultationLink } = useUrlHelper();

const showConsultationModal = ref<boolean>(false);
const showMedicareAppointmentModal = ref<boolean>(false);

const showOfficePhone = computed(() => pageState.value.officePhoneRoutesEnabled.includes('/contact-us'));
const showFeedbackButton = computed(() => pageState.value.globalSettings?.feedback.globalFeedbackButton.enabled);

const goToLink = (url: string): void => {
  window.location.href = url;
};

onBeforeMount(() => {
  const script = document.createElement('script');
  script.src = 'https://static.elfsight.com/platform/platform.js';
  document.head.appendChild(script);
});
</script>

<template>
  <div class="flex flex-1 flex-col bg-white items-center">
    <div class="flex lg:hidden py-4">
      <span class="text-2xl text-slate-400">Lowest Prices. Simple Process.</span>
    </div>
    <div class="flex flex-col max-w-7xl mx-auto space-y-10 relative bg-white">
      <div class="flex absolute w-full lg:w-52 h-14 lg:h-12 bg-blue-500 font-semibold text-4xl lg:text-2xl text-white px-4 py-2 justify-center lg:justify-start lg:rounded-r-full z-10 lg:mt-16">
        <div class="hidden lg:block absolute right-full top-0 h-full w-screen bg-blue-500"></div>
        Contact Us
      </div>

      <div class="flex flex-col w-full lg:flex-row space-x-0 lg:space-x-6 space-y-6 lg:space-y-0 px-4">
        <div class="flex flex-col w-full lg:w-3/8 space-y-6 mt-6 lg:mt-24">
          <ContactCard v-if="showFeedbackButton || showOfficePhone" title="Covered California Health Plans" :phone="pageState.officePhoneNumber" button-text="Message Us" :show-phone="showOfficePhone">
            <ul v-if="showOfficePhone" class="mt-1 text-xl">
              <li class="text-blue-600 font-semibold">Options:</li>
              <li>Option 2 - Individual & Family</li>
              <li>Option 3 - Small Business</li>
            </ul>
            <feedback-modal v-if="showFeedbackButton" class="" display-mode="link">
              <SecondaryButton class="mt-2" tone="strong" label="Message Us" />
            </feedback-modal>
          </ContactCard>

          <ContactCard title="Medicare Health Plans" subtitle="(Senior 65+ or Disabled)" phone="1-800-352-4784" button-text="Sign Up for a Webinar" :button-link="(): boolean => showMedicareAppointmentModal = true" />

          <ContactCard title="Small Business" phone="1-707-526-0448" button-text="Schedule a Consultation" :button-link="(): boolean => showConsultationModal = true" />
        </div>
        <div class="flex flex-col w-full lg:w-5/8 space-y-6">
          <div class="relative w-full lg:w-[calc(100%+5rem)] lg:mr-[-5rem]">
            <img
              :src="getCdnUrl('/assets/img/contactUs/hfc_people.webp')"
              class="w-full object-cover"
            />
          </div>
          <div>
            <AccordionContent class="w-full border-blue-500 last:border-b-2" container-class="p-4 border-t-2 border-blue-500" closed-class="bg-white" closed-icon="chevron-down" closed-icon-color="text-blue-900" open-icon="chevron-up" open-icon-color="text-blue-900">
              <template #title>
                <span class="text-blue-500 text-normal lg:text-xl">Health Insurance Services</span>
              </template>
              <template #content>
                <div>
                  <p>All licensed agents are certified through the Department of Insurance and Covered California. Representatives are available to assist you in getting affordable CA health insurance coverage for yourself, your family or for your small business.</p>
                  <br />
                  <table class="w-full border-separate border-spacing-y-2">
                    <tbody>
                      <tr>
                        <td class="font-semibold">Need to enroll on Medi-Cal?</td>
                        <td><a :href="getMediCalQuoteUrl()">Click Here</a></td>
                      </tr>
                      <tr v-if="showOfficePhone">
                        <td class="font-semibold">Need to get quotes or enroll on a Covered California plan?</td>
                        <td>{{ pageState.officePhoneNumber }} option 2</td>
                      </tr>
                      <tr v-if="showOfficePhone">
                        <td class="font-semibold">Need to change plans?</td>
                        <td>{{ pageState.officePhoneNumber }} option 2</td>
                      </tr>
                      <tr v-if="showOfficePhone">
                        <td class="font-semibold">Need small business coverage?</td>
                        <td>{{ pageState.officePhoneNumber }} option 3</td>
                      </tr>
                    </tbody>
                  </table>
                  <br />
                  <span class="font-semibold">Address:</span>
                  <p>Health for California Insurance Center</p>
                  <p>For walk in support, use this address:</p>
                  <p>4000 Montgomery Dr Ste D7</p>
                  <p>Santa Rosa, CA 95405</p>
                  <br />
                  <span class="font-semibold">License:</span>
                  <p>Health for California Insurance Center</p>
                  <p>CA License #0G13531</p>
                </div>
              </template>
            </AccordionContent>

            <AccordionContent class="w-full border-blue-500 last:border-b-2" container-class="p-4 border-t-2 border-blue-500" closed-class="bg-white" closed-icon="chevron-down" closed-icon-color="text-blue-900" open-icon="chevron-up" open-icon-color="text-blue-900">
              <template #title>
                <span class="text-blue-500 text-normal lg:text-xl">Medi-Cal Assistance</span>
              </template>
              <template #content>
                <div>
                  <table class="w-full border-separate border-spacing-y-2">
                    <tbody v-if="showOfficePhone">
                      <tr>
                        <td class="font-semibold">Need to enroll on Medi-Cal?</td>
                        <td><a :href="getMediCalQuoteUrl()">Click Here</a></td>
                      </tr>
                      <tr>
                        <td class="font-semibold">Need Medi-Cal assistance?</td>
                        <td>{{ pageState.officePhoneNumber }} option 1</td>
                      </tr>
                    </tbody>
                    <div v-else>
                      <p>To enroll on Medi-cal, check Medi-cal enrollment status, report changes, cancel enrollment, or find out about Medi-cal coverage, <a href="https://www.dhcs.ca.gov/services/medi-cal/Pages/CountyOffices.aspx">call or visit your local county human services office.</a></p>
                    </div>
                  </table>
                </div>
              </template>
            </AccordionContent>

            <AccordionContent class="w-full border-blue-500 last:border-b-2" container-class="p-4 border-t-2 border-blue-500" closed-class="bg-white" closed-icon="chevron-down" closed-icon-color="text-blue-900" open-icon="chevron-up" open-icon-color="text-blue-900">
              <template #title>
                <span class="text-blue-500 text-normal lg:text-xl">Senior Health Plans & Medicare</span>
              </template>
              <template #content>
                <div>
                  <span class="font-semibold">Medicare Support:</span>
                  <p>A licensed insurance agent is available to assist you.</p>
                  <br />
                  <ul class="list-disc pl-5">
                    <li>Call <a class="text-black font-semibold" href="tel:1-800-352-4784">1-800-352-4784</a></li>
                    <li><a :href="getMedicareAppointmentUrl()">Schedule an Appointment</a></li>
                    <li><a @click="(): boolean => showMedicareAppointmentModal = true">Sign Up for a Webinar</a></li>
                    <li><a :href="getIaFQuoteUrl()">Get a Quote</a></li>
                  </ul>
                </div>
              </template>
            </AccordionContent>
            <AccordionContent class="w-full" container-class="p-4 border-t-2 border-blue-500" closed-class="bg-white" closed-icon="chevron-down" closed-icon-color="text-blue-900" open-icon="chevron-up" open-icon-color="text-blue-900">
              <template #title>
                <span class="text-blue-500 text-normal lg:text-xl">Other Resources</span>
              </template>
              <template #content>
                <div class="px-2 space-y-2">
                  <AccordionContent class="w-full" container-class="pb-2 border-b border-blue-500" closed-class="bg-white" closed-icon="circle-right" closed-icon-color="text-blue-900" open-icon="chevron-up" open-icon-color="text-blue-900">
                    <template #title>
                      <span class="text-blue-500 font-semibold text-normal">How to Pay Help</span>
                    </template>
                    <template #content>
                      <div class="mb-4">
                        <p>To activate your CA health insurance coverage, you must pay the first month’s premium by a certain due date. If you have not made your first payment, contact the carrier you enrolled with to make payment.</p>
                        <br />
                        <table class="w-full border-separate border-spacing-y-2">
                          <tbody>
                            <tr>
                              <td class="font-semibold">Aetna</td>
                              <td class="flex w-full justify-end"><a class="text-black" :href="`tel:8669012922`">(866) 901-2922</a></td>
                            </tr>
                            <tr>
                              <td class="font-semibold">Balance (CCHP)</td>
                              <td class="flex w-full justify-end"><a class="text-black" :href="`tel:8887757888`">(888) 775-7888</a></td>
                            </tr>
                            <tr>
                              <td class="font-semibold">Blue Shield of California</td>
                              <td class="flex w-full justify-end"><a class="text-black" :href="`tel:8558369705`">(855) 836-9705</a></td>
                            </tr>
                            <tr>
                              <td class="font-semibold">Health Net</td>
                              <td class="flex w-full justify-end"><a class="text-black" :href="`tel:8889264988`">(888) 926-4988</a></td>
                            </tr>
                            <tr>
                              <td class="font-semibold">IEHP</td>
                              <td class="flex w-full justify-end"><a class="text-black" :href="`tel:8554334347`">(855) 433-4347</a></td>
                            </tr>
                            <tr>
                              <td class="font-semibold">Kaiser Permanente</td>
                              <td class="flex w-full justify-end"><a class="text-black" :href="`tel:8445247370`">(844) 524-7370</a></td>
                            </tr>
                            <tr>
                              <td class="font-semibold">L.A. Care Health Plan</td>
                              <td class="flex w-full justify-end"><a class="text-black" :href="`tel:8552702327`">(855) 270-2327</a></td>
                            </tr>
                            <tr>
                              <td class="font-semibold">Molina Healthcare</td>
                              <td class="flex w-full justify-end"><a class="text-black" :href="`tel:8888582150`">(888) 858-2150</a></td>
                            </tr>
                            <tr>
                              <td class="font-semibold">Sharp</td>
                              <td class="flex w-full justify-end"><a class="text-black" :href="`tel:8003592002`">(800) 359-2002</a></td>
                            </tr>
                            <tr>
                              <td class="font-semibold">Western Health Advantage</td>
                              <td class="flex w-full justify-end"><a class="text-black" :href="`tel:8884422206`">(888) 442-2206</a></td>
                            </tr>
                            <tr>
                              <td class="font-semibold">Valley Health Plan</td>
                              <td class="flex w-full justify-end"><a class="text-black" :href="`tel:8884218444`">(888) 421-8444</a></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </template>
                  </AccordionContent>

                  <AccordionContent class="w-full" container-class="pb-2 border-b border-blue-500" closed-class="bg-white" closed-icon="circle-right" closed-icon-color="text-blue-900" open-icon="chevron-up" open-icon-color="text-blue-900">
                    <template #title>
                      <span class="text-blue-500 font-semibold text-normal">Dental Plan Phone Numbers and Websites</span>
                    </template>
                    <template #content>
                      <div class="flex flex-col mb-4">
                        <p>Your dental plan is there to answer your questions. They can help you find a dentist, explain networks, help you switch dentists, and answer questions about coverage and many other things.</p>
                        <br />
                        <p>For billing related questions regarding due dates, setting up auto-pay and setting up an online member account (if offered) please contact them using the contact information below.</p>
                        <br />
                        <div>
                          <a @click="goToLink(getEnrollmentAppUri('/dental/quote'))" class="px-4 py-2 mt-2 bg-blue-900 text-white rounded-full">
                            Get a Quote
                          </a>
                        </div>

                        <br />
                        <div class="flex flex-col space-y-4">
                          <div class="grid grid-cols-3 w-full">
                            <div class="flex flex-col col-span-2">
                              <span class="font-semibold">Access Dental Plan</span>
                              <a href="www.premierlife.com">www.premierlife.com</a>
                            </div>
                            <div class="flex w-full justify-end"><a class="text-black" href="tel:(844) 561-5600">(844) 561-5600</a></div>
                          </div>
                          <div class="grid grid-cols-3 w-full">
                            <div class="flex flex-col col-span-2">
                              <span class="font-semibold">Blue Shield Dental</span>
                              <a href="https://www.blueshieldca.com/en/ifp/dental-plans">https://www.blueshieldca.com/en/ifp/dental-plans</a>
                            </div>
                            <div class="flex w-full justify-end"><a class="text-black" href="tel:8882734546">(888) 273-4546</a></div>
                          </div>
                          <div class="grid grid-cols-3 w-full">
                            <div class="flex flex-col col-span-2">
                              <span class="font-semibold">California Dental Network</span>
                              <a href="http://www.caldental.net">www.caldental.net</a>
                            </div>
                            <div class="flex w-full justify-end"><a class="text-black" href="tel:8554254164">(855) 425-4164</a></div>
                          </div>
                          <div class="grid grid-cols-3 w-full">
                            <div class="flex flex-col col-span-2">
                              <span class="font-semibold">Delta Dental of California</span>
                              <a href="http://www.deltadentalins.com">www.deltadentalins.com</a>
                            </div>
                            <div class="flex w-full justify-end"><a class="text-black" href="tel:8007656003">(800) 765-6003</a></div>
                          </div>
                          <div class="grid grid-cols-3 w-full">
                            <div class="flex flex-col col-span-2">
                              <span class="font-semibold">Dental Health Services</span>
                              <a href="http://www.dentalhealthservices.com">www.dentalhealthservices.com</a>
                            </div>
                            <div class="flex w-full justify-end"><a class="text-black" href="tel:8554950905">(855) 495-0905</a></div>
                          </div>
                          <div class="grid grid-cols-3 w-full">
                            <div class="flex flex-col col-span-2">
                              <span class="font-semibold">Premier Access</span>
                              <a href="http://www.premierlife.com">www.premierlife.com</a>
                            </div>
                            <div class="flex w-full justify-end"><a class="text-black" href="tel:8445615600">(844) 561-5600</a></div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </AccordionContent>

                  <AccordionContent class="w-full" container-class="pb-2 border-b border-blue-500" closed-class="bg-white" closed-icon="circle-right" closed-icon-color="text-blue-900" open-icon="chevron-up" open-icon-color="text-blue-900">
                    <template #title>
                      <span class="text-blue-500 font-semibold text-normal">Vision Plans</span>
                    </template>
                    <template #content>
                      <div class="space-y-4 mb-4">
                        <p>Find vision insurance online. Coverage includes eye exams, quality networks, coverage for eye glasses and lenses.</p>
                        <div v-if="showOfficePhone" class="text-lg">
                          <a class="text-black font-semibold" href="tel:{{ pageState.officePhoneNumber }}">{{ pageState.officePhoneNumber }}</a>
                        </div>
                        <p><a @click="goToLink(getEnrollmentAppUri('/vision/quote'))" class="px-4 py-2 bg-blue-900 text-white rounded-full">Get a Quote</a></p>
                        <p><a :href="getBaseUrl('/supplemental-plans/vision-insurance/vsp')">VSP</a></p>
                        <p><a :href="getBaseUrl('/supplemental-plans/vision-insurance/eyemed')">EyeMed</a></p>
                      </div>
                    </template>
                  </AccordionContent>

                  <AccordionContent class="w-full" container-class="pb-2 border-b border-blue-500" closed-class="bg-white" closed-icon="circle-right" closed-icon-color="text-blue-900" open-icon="chevron-up" open-icon-color="text-blue-900">
                    <template #title>
                      <span class="text-blue-500 font-semibold text-normal">Short Term/Travel Plans</span>
                    </template>
                    <template #content>
                      <div class="mb-4">
                        <p>You can enroll quickly and easily online for short term / travel CA health insurance plans.</p>
                        <br />
                        <p>Get quotes and enroll:</p>
                        <ul class="list-disc pl-5">
                          <li><a :href="getBaseUrl('/supplemental-plans/short-term-health-insurance')">Short Term Health Insurance</a></li>
                          <li><a :href="getBaseUrl('/supplemental-plans/travel-health-insurance')">Travel Health Insurance</a></li>
                        </ul>
                      </div>
                    </template>
                  </AccordionContent>

                  <AccordionContent class="w-full" container-class="pb-2 border-b border-blue-500" closed-class="bg-white" closed-icon="circle-right" closed-icon-color="text-blue-900" open-icon="chevron-up" open-icon-color="text-blue-900">
                    <template #title>
                      <span class="text-blue-500 font-semibold text-normal">Submit Requested Documents to Covered California</span>
                    </template>
                    <template #content>
                      <div class="flex flex-col mb-4">
                        <p>If you were requested to submit documents to Covered California to verify certain information on your application, you can submit these 1 of 3 ways.</p>
                        <br />
                        <span class="font-semibold text-lg mb-2">Submit documents online</span>
                        <p>You can upload your documents by taking a legible photo with a smart phone then saving to a computer.</p>
                        <ul class="list-decimal lining-nums pl-5 mx-4">
                          <li><a href="https://apply.coveredca.com/apspahbx/login.portal">Login to your online application</a></li>
                          <li>Click on the “Manage Verifications” link located on the right, below “Actions”</li>
                          <li>Click “Submit Verifications”</li>
                          <li>Click the upload link on the Submit Verifications page</li>
                          <li>Select the “Document Category” of the document you want to upload</li>
                          <li>Choose the “Document Type” in the dropdown menu</li>
                          <li>Click the “Browse” button to find the document, then upload</li>
                          <li>Look for the text in green that says, “File uploaded successfully”</li>
                          <li>Click the “View” option to verify the correct document was uploaded</li>
                        </ul>
                        <br />
                        <span class="font-semibold text-lg mb-2">Submit documents by fax</span>
                        <p>Please include the <a href="https://www.coveredca.com/PDFs/DocumentCoverPage.pdf">Document Cover Page</a> when you fax your documents, and make sure each copy of the document is legible before faxing.</p>
                        <div class="mt-2 text-lg"><a class="text-black" href="tel:8003293700">(800) 329-3700</a></div>
                        <br />
                        <span class="font-semibold text-lg mb-2">Submit documents by mail</span>
                        <p>Please include the <a href="https://www.coveredca.com/PDFs/DocumentCoverPage.pdf">Document Cover Page</a> if you mail your documents, and make sure each copy of the document is legible before mailing.</p>
                        <br />
                        <p>Covered California</p>
                        <p>P.O. Box 989725</p>
                        <p>West Sacramento, CA</p>
                        <p>95798-9725</p>
                      </div>
                    </template>
                  </AccordionContent>

                  <AccordionContent class="w-full" container-class="" closed-class="bg-white" closed-icon="circle-right" closed-icon-color="text-blue-900" open-icon="chevron-up" open-icon-color="text-blue-900">
                    <template #title>
                      <span class="text-blue-500 font-semibold text-normal">Health Plan Phone Numbers and Websites</span>
                    </template>
                    <template #content>
                      <div class="flex flex-col mb-4">
                        <p>Your CA health insurance plan is there is to answer your questions. They can help you find a doctor, explain doctor and hospital networks, help you switch doctors and answer questions about coverage and many other things.</p>
                        <br />
                        <p>For billing related questions regarding due dates, setting up auto-pay and setting up an online member account (if offered) please contact them using the contact information below.</p>
                        <br />
                        <div class="flex flex-col space-y-4">
                          <div class="grid grid-cols-3 w-full">
                            <div class="flex flex-col col-span-2">
                              <span class="font-semibold">Aetna</span>
                              <a href="https://www.aetnacvshealth.com">www.aetnacvshealth.com</a>
                            </div>
                            <div class="flex w-full justify-end"><a class="text-black" href="tel:8669012922">(866) 901-2922</a></div>
                          </div>
                          <div class="grid grid-cols-3 w-full">
                            <div class="flex flex-col col-span-2">
                              <span class="font-semibold">Balance (CCHP)</span>
                              <a href="https://www.cchphealthplan.com">www.cchphealthplan.com</a>
                            </div>
                            <div class="flex w-full justify-end"><a class="text-black" href="tel:8887757888">(888) 775-7888</a></div>
                          </div>
                          <div class="grid grid-cols-3 w-full">
                            <div class="flex flex-col col-span-2">
                              <span class="font-semibold">Blue Shield of California</span>
                              <a href="https://www.blueshieldca.com">www.blueshieldca.com</a>
                            </div>
                            <div class="flex w-full justify-end"><a class="text-black" href="tel:8558369705">(855) 836-9705</a></div>
                          </div>
                          <div class="grid grid-cols-3 w-full">
                            <div class="flex flex-col col-span-2">
                              <span class="font-semibold">Health Net</span>
                              <a href="https://www.healthnet.com">www.healthnet.com</a>
                            </div>
                            <div class="flex w-full justify-end"><a class="text-black" href="tel:8772889082">(877) 288-9082</a></div>
                          </div>
                          <div class="grid grid-cols-3 w-full">
                            <div class="flex flex-col col-span-2">
                              <span class="font-semibold">IEHP</span>
                              <a href="https://www.iehp.org">www.iehp.org</a>
                            </div>
                            <div class="flex w-full justify-end"><a class="text-black" href="tel:8554334347">(855) 433-4347</a></div>
                          </div>
                          <div class="grid grid-cols-3 w-full">
                            <div class="flex flex-col col-span-2">
                              <span class="font-semibold">Kaiser Permanente</span>
                              <a href="https://www.kp.org">www.kp.org</a>
                            </div>
                            <div class="flex w-full justify-end"><a class="text-black" href="tel:8004644000">(800) 464-4000</a></div>
                          </div>
                          <div class="grid grid-cols-3 w-full">
                            <div class="flex flex-col col-span-2">
                              <span class="font-semibold">L.A. Care Health Plan</span>
                              <a href="https://www.lacare.org">www.lacare.org</a>
                            </div>
                            <div class="flex w-full justify-end"><a class="text-black" href="tel:8884522273">(888) 452-2273</a></div>
                          </div>
                          <div class="grid grid-cols-3 w-full">
                            <div class="flex flex-col col-span-2">
                              <span class="font-semibold">Molina Healthcare</span>
                              <a href="https://www.molinahealthcare.com">www.molinahealthcare.com</a>
                            </div>
                            <div class="flex w-full justify-end"><a class="text-black" href="tel:8885625442">(888) 562-5442</a></div>
                          </div>
                          <div class="grid grid-cols-3 w-full">
                            <div class="flex flex-col col-span-2">
                              <span class="font-semibold">Sharp</span>
                              <a href="https://www.sharphealthplan.com">www.sharphealthplan.com</a>
                            </div>
                            <div class="flex w-full justify-end"><a class="text-black" href="tel:8003592002">(800) 359-2002</a></div>
                          </div>
                          <div class="grid grid-cols-3 w-full">
                            <div class="flex flex-col col-span-2">
                              <span class="font-semibold">Western Health Advantage</span>
                              <a href="https://www.westernhealth.com">www.westernhealth.com</a>
                            </div>
                            <div class="flex w-full justify-end"><a class="text-black" href="tel:8885632250">(888) 563-2250</a></div>
                          </div>
                          <div class="grid grid-cols-3 w-full">
                            <div class="flex flex-col col-span-2">
                              <span class="font-semibold">Valley Health Plan</span>
                              <a href="https://www.valleyhealthplan.org">www.valleyhealthplan.org</a>
                            </div>
                            <div class="flex w-full justify-end"><a class="text-black" href="tel:8884218444">(888) 421-8444</a></div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </AccordionContent>
                </div>
              </template>
            </AccordionContent>
          </div>
        </div>
      </div>

      <div class="relative flex flex-col w-full aspect-video items-center justify-center">
        <div id="test" class="flex flex-col lg:absolute lg:right-0 lg:top-1/2 lg:-translate-y-1/2 items-center justify-end rounded-md border-blue-300 bg-blue-500 text-white z-20 py-4 lg:py-8 px-4 lg:px-10">
          <div class="flex justify-center w-full font-semibold text-sm sm:text-3xl">
            Address & Hours Of Operation
          </div>
          <div class="flex justify-center w-full my-3">
            <div class="w-full bg-white h-0.5"></div>
          </div>
          <div class="flex flex-col w-full text-sm sm:text-2xl space-y-8">
            <p>Monday – Friday, 9:00 am – 5:30 pm</p>
            <p>Address:<br>Health for California Insurance Center</p>
            <p>For walk in support, use this address:<br>4000 Montgomery Dr Ste D7<br>Santa Rosa, CA 95405</p>
          </div>
        </div>
        <img
          class="lg:absolute lg:left-0 lg:top-1/2 lg:-translate-y-1/2 w-full lg:w-2/3 z-10 -mt-20 sm:-mt-40 lg:-mt-0 object-cover"
          :src="getCdnUrl('/assets/img/contactUs/contact_us_map.webp')"
          style="object-position: left center;"
        />
      </div>

      <GoogleReviews />
      <ServicesSection />
      <LicenseContent />
    </div>
  </div>
  <ModalDialog v-if="showConsultationModal == true" :show-confirm-button="false" size="xl" title="Schedule a Consultation" @close="showConsultationModal = false">
    <template #content>
      <iframe class="w-full h-full md:min-h-[calc(50vh)] lg:-mt-16" style="zoom: 1.4;" :src="getSmallBusinessConsultationLink()"></iframe>
    </template>
  </ModalDialog>
  <ModalDialog v-if="showMedicareAppointmentModal == true" :show-confirm-button="false" size="xl" title="Schedule a Medicare Appointment" @close="showMedicareAppointmentModal = false">
    <template #content>
      <div class="h-[calc(100vh-190px)] -m-4">
        <webinar-questioner />
      </div>
    </template>
  </ModalDialog>
</template>
